import { EditNewIcon } from '@app/components/icons/edit-new';
import { Spinner } from '@app/components/icons/spinner';
import { Tooltip } from '@app/components/tooltip';
import { EventSpeaker } from '@app/routes/content/domain';
import { Profile } from '@app/routes/content/routes/recordings/components/icons';
import { TranscriptSpeaker } from '@app/routes/content/routes/recordings/domain';
import { selectSpeakerManagement } from '@app/routes/content/routes/recordings/selector';
import { getContentHubMediaHost } from '@app/utilities/utils';
import { useTranslation } from '@hopin-team/provider-translation';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

type SpeakerDetailsProps = {
  speaker: EventSpeaker | TranscriptSpeaker;
  speakerKey: string;
};

export const SpeakerDetails = ({
  speaker,
  speakerKey,
}: SpeakerDetailsProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const speakerManagement = useSelector(selectSpeakerManagement);
  const isEventSpeaker = 'firstName' in speaker && 'lastName' in speaker;
  const displayName = isEventSpeaker
    ? `${speaker.firstName} ${speaker.lastName}`.trim()
    : speaker.name;

  const headline = isEventSpeaker ? speaker.headline : '';
  const imageUrl = isEventSpeaker ? speaker.imageUrl : null;

  const isManualSpeaker =
    isEventSpeaker &&
    'id' in speaker &&
    speaker.id !== null &&
    speaker.eventSpeakerId === null;

  return (
    <div className="flex gap-1.5 items-center justify-start rounded-lg w-full pr-2">
      <div className="bg-spring-neutral-b3 flex items-center justify-center rounded-full shrink-0 size-6">
        <div className="bg-spring-neutral-b4 overflow-clip relative rounded-full shrink-0 size-6">
          {speakerManagement.status === 'processing' &&
          speakerManagement.speakerKey === speakerKey ? (
            <div className="absolute left-1/2 top-1/2 translate-x-[-50%] translate-y-[-50%]">
              <Spinner className="w-3 h-3 text-white" />
            </div>
          ) : imageUrl ? (
            <div
              className="absolute bg-center bg-cover bg-no-repeat inset-0 rounded-full"
              style={{
                backgroundImage: `url('${
                  imageUrl.startsWith('https')
                    ? imageUrl
                    : `${getContentHubMediaHost()}${imageUrl}`
                }')`,
              }}
            />
          ) : (
            <div className="absolute left-1/2 overflow-clip size-3 top-1/2 translate-x-[-50%] translate-y-[-50%]">
              <Profile className="w-3 h-3 text-white" />
            </div>
          )}
        </div>
      </div>

      <div
        className="font-medium text-spring-neutral-b1 text-sm"
        title={displayName}
      >
        {displayName}
      </div>

      {headline && (
        <>
          <div className="flex items-center justify-center relative shrink-0">
            <div className="w-1 h-1 rounded-full bg-spring-neutral-b1/50" />
          </div>
          <div
            className="font-medium text-xs text-spring-neutral-b1/50 truncate"
            title={headline}
          >
            {headline}
          </div>
        </>
      )}

      {isManualSpeaker && (
        <div className="ml-auto flex items-center justify-center">
          <Tooltip tooltip={t('content-hub.update-speaker')}>
            <EditNewIcon
              className="w-4 h-4 text-spring-neutral-b1/70 hover:text-spring-neutral-b1 cursor-pointer"
              onClick={() => {
                dispatch({
                  type: '[recording] edit manual speaker clicked',
                  payload: {
                    speakerKey,
                    speaker: speaker as EventSpeaker,
                  },
                });
              }}
            />
          </Tooltip>
        </div>
      )}
    </div>
  );
};
